<template>
  <div class="task-detail-page">
    <div class="page-header">
      <span class="page-title">任务详情</span>
      <a-button type="primary" @click="goBack">
        返回
        <template #icon><ArrowLeftOutlined /></template>
      </a-button>
    </div>

    <div class="page-body">
      <!-- 左侧：任务信息 -->
      <div class="left-panel">
        <a-spin :spinning="detailLoading">
          <!-- 基础信息 -->
          <div class="form-section">
            <div class="section-title">基础信息</div>
            <div class="info-row">
              <span class="info-label">车辆编码</span>
              <span class="info-value">{{ detail.licenceNo || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">运营企业</span>
              <span class="info-value">{{ detail.operatorName || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">安全员</span>
              <span class="info-value">{{ securityUserNames || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">运营日期</span>
              <span class="info-value">{{ dateRangeText }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">运营时段</span>
              <span class="info-value">{{ operationTimesText }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">任务状态</span>
              <span class="info-value">
                <a-tag :color="statusOption.color">{{ statusOption.label }}</a-tag>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">创建时间</span>
              <span class="info-value">{{ detail.createTime || '-' }}</span>
            </div>
            <div class="info-row info-row-block">
              <span class="info-label">任务描述</span>
              <span class="info-value">{{ detail.description || '-' }}</span>
            </div>
          </div>

          <!-- 线路信息 -->
          <div class="form-section">
            <div class="section-title">线路信息</div>
            <div class="info-row point-row">
              <span class="point-dot start-dot"></span>
              <span class="info-label">起点</span>
              <span class="info-value">{{ detail.originStopName || '-' }}</span>
            </div>
            <div v-if="waypoints.length > 0" class="waypoint-list">
              <div
                  v-for="(point, idx) in waypoints"
                  :key="idx"
                  class="info-row point-row"
              >
                <span class="point-index">{{ idx + 1 }}</span>
                <span class="info-label">途经点</span>
                <span class="info-value">{{ point.stopName || point.name || '-' }}</span>
              </div>
            </div>
            <div class="info-row point-row">
              <span class="point-dot end-dot"></span>
              <span class="info-label">终点</span>
              <span class="info-value">{{ detail.destinationStopName || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">路线名称</span>
              <span class="info-value">{{ detail.routeName || '-' }}</span>
            </div>
          </div>

          <!-- 审核提交 -->
          <div class="form-section">
            <div class="section-title">审核提交</div>
            <a-form layout="vertical">
              <a-form-item label="审核意见" required>
                <a-textarea
                  v-model:value="reviewForm.comment"
                  :rows="4"
                  :maxlength="500"
                  show-count
                  placeholder="请输入审核意见"
                />
              </a-form-item>
              <div class="review-actions">
                <a-button
                  type="primary"
                  :loading="reviewLoading"
                  @click="handleReview(1)"
                >
                  通过
                </a-button>
                <a-button
                  type="primary"
                  danger
                  :loading="reviewLoading"
                  class="ml16"
                  @click="handleReview(-1)"
                >
                  驳回
                </a-button>
              </div>
            </a-form>
          </div>
        </a-spin>
      </div>

      <!-- 右侧：地图区域 -->
      <div class="right-panel">
        <div id="task-detail-map" class="map-container"></div>
        <div v-if="mapLoading" class="loading-overlay">
          <a-spin size="large" />
          <span class="loading-text">地图加载中...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { ArrowLeftOutlined } from "@ant-design/icons-vue";
import AMapLoader from "@amap/amap-jsapi-loader";
import { getTaskDetail, reviewTask } from "@/api/task/task.js";
import { getFenceList } from "@/api/roads/fence.js";

// 高德地图安全密钥（必须在 AMapLoader.load 之前设置）
window._AMapSecurityConfig = {
  securityJsCode: "cd0893188bd79a43d8e4539aec2e2b2a",
};

const route = useRoute();
const router = useRouter();

const detailLoading = ref(false);
const detail = ref({});
const mapLoading = ref(false);
let map = null;
let AMapInstance = null;
// 围栏覆盖物（作为地图背景参考图层）
let fenceOverlays = [];

// 解析围栏几何数据
const parseFenceGeometry = (fence) => {
  if (!fence.geometry) return null;
  try {
    return typeof fence.geometry === "string" ? JSON.parse(fence.geometry) : fence.geometry;
  } catch (error) {
    console.error("解析围栏几何数据失败:", error);
    return null;
  }
};

// 创建围栏覆盖物（参考 roads/fence/index.vue 的默认样式）
const createFenceOverlay = (geometry) => {
  const { type, coordinates } = geometry;
  if (type === "Polygon") {
    const ring = coordinates[0] || [];
    const path = ring.map(([lng, lat]) => new AMapInstance.LngLat(lng, lat));
    return new AMapInstance.Polygon({
      path,
      fillColor: "#ff4d4f",
      fillOpacity: 0.15,
      strokeColor: "#ff4d4f",
      strokeWeight: 2,
    });
  } else if (type === "LineString") {
    const path = coordinates.map(([lng, lat]) => new AMapInstance.LngLat(lng, lat));
    return new AMapInstance.Polyline({
      path,
      strokeColor: "#ff4d4f",
      strokeWeight: 2,
    });
  } else if (type === "Point") {
    const [lng, lat] = coordinates;
    return new AMapInstance.Marker({ position: [lng, lat] });
  }
  return null;
};

// 加载围栏列表并绘制到地图上
const loadFences = async () => {
  try {
    const res = await getFenceList();
    if (res.code === 200 && Array.isArray(res.data)) {
      res.data.forEach((fence) => {
        const geometry = parseFenceGeometry(fence);
        if (!geometry) return;
        const overlay = createFenceOverlay(geometry);
        if (overlay) {
          overlay.setMap(map);
          fenceOverlays.push(overlay);
        }
      });
    }
  } catch (error) {
    console.error("获取围栏列表失败:", error);
  }
};

// 地图 clearMap 后重新挂载围栏覆盖物
const redrawFences = () => {
  if (!map) return;
  fenceOverlays.forEach((o) => {
    try { o.setMap(map); } catch (e) { /* ignore */ }
  });
};

// 状态选项
const statusOptions = [
  { label: "待审核", value: 0, color: "processing" },
  { label: "已审核", value: 1, color: "success" },
];

const statusOption = computed(() => {
  const val = detail.value.status;
  return (
      statusOptions.find((o) => o.value === val) || {
        label: "-",
        color: "default",
      }
  );
});

// 安全员名称拼接
const securityUserNames = computed(() => {
  const users = detail.value.securityUsers || [];
  if (!users.length) return "";
  return users.map((u) => u.name).filter(Boolean).join("、");
});

// 运营日期
const dateRangeText = computed(() => {
  const { startDate, endDate } = detail.value;
  if (!startDate && !endDate) return "-";
  return `${startDate || ""} ~ ${endDate || ""}`;
});

// 运营时段
const operationTimesText = computed(() => {
  const times = parsedOperationTimes.value;
  if (!times.length) return "-";
  return times.map((s) => `${s.start}~${s.end}`).join("、");
});

// 解析运营时段（接口返回为 JSON 字符串）
const parsedOperationTimes = computed(() => {
  const raw = detail.value.operationTimes;
  if (!raw) return [];
  try {
    if (Array.isArray(raw)) return raw;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
});

// 途经点（详情数据中 waypoints 可能是 { stopId, stopName, name } 的数组）
const waypoints = computed(() => {
  return detail.value.waypoints || [];
});

// 加载高德地图 SDK
const loadAMapSDK = async () => {
  const opts = {
    key: "6588d9e5b6ae0751c799cb4c7b3e30be",
    version: "2.0",
    plugins: ["AMap.ToolBar", "AMap.Scale"],
  };
  if (typeof AMapLoader.reset === "function") {
    AMapLoader.reset();
  }
  return await AMapLoader.load(opts);
};

// 初始化地图
const initMap = async () => {
  mapLoading.value = true;
  try {
    const mapContainer = document.getElementById("task-detail-map");
    if (
        mapContainer &&
        (mapContainer.offsetWidth === 0 || mapContainer.offsetHeight === 0)
    ) {
      mapContainer.style.minHeight = "400px";
      await nextTick();
    }

    const AMap = await loadAMapSDK().catch((error) => {
      throw new Error(`高德地图API加载失败: ${error.message || error}`);
    });

    AMapInstance = AMap;
    map = new AMap.Map("task-detail-map", {
      zoom: 12,
      center: [103.8349, 36.068],
      mapStyle: "amap://styles/whitesmoke",
      viewMode: "2D",
      dragEnable: true,
      animateEnable: true,
    });

    map.addControl(
        new AMap.ToolBar({ position: "RB", offset: [10, 60], liteStyle: true })
    );
  } catch (error) {
    console.error("地图初始化失败:", error);
    message.error(error.message || "地图初始化失败，请刷新页面重试");
  } finally {
    mapLoading.value = false;
  }
};

// 绘制路线和点位
const drawRouteAndMarkers = () => {
  if (!map || !AMapInstance || !detail.value) return;

  map.clearMap();
  redrawFences();
  const points = [];
  const routeOverlays = [];

  // 绘制 paths 路线（导航样式：带箭头的折线）
  const paths = detail.value.paths || [];
  if (paths.length > 0) {
    // 主路线（蓝色粗线）
    const polyline = new AMapInstance.Polyline({
      path: paths,
      strokeColor: "#1890ff",
      strokeWeight: 6,
      strokeOpacity: 0.9,
      lineJoin: "round",
      lineCap: "round",
      showDir: true,
      map: map,
    });
    // 路线外发光底层（浅色描边，增加导航感）
    const polylineBg = new AMapInstance.Polyline({
      path: paths,
      strokeColor: "#ffffff",
      strokeWeight: 10,
      strokeOpacity: 0.6,
      lineJoin: "round",
      lineCap: "round",
      map: map,
      zIndex: 1,
    });
    polylineBg.setMap(map);
    polyline.setMap(map);
    routeOverlays.push(polyline);
  }

  // 起点（绿色）使用 originStopGcj02Coordinates
  const originCoord = detail.value.originStopGcj02Coordinates;
  const originName = (detail.value.originStopName || "").trim();
  if (originCoord && Array.isArray(originCoord) && originCoord.length === 2) {
    const marker = new AMapInstance.Marker({
      position: originCoord,
      content: `<div style="display:flex;align-items:center;gap:4px;white-space:nowrap;"><div style="width:28px;height:28px;border-radius:50%;background:#52c41a;color:#fff;font-size:12px;line-height:28px;text-align:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">起</div><span style="font-size:12px;color:#333;background:rgba(255,255,255,0.9);padding:2px 6px;border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.2);">${originName}</span></div>`,
      offset: new AMapInstance.Pixel(-14, -14),
      map: map,
    });
    points.push(originCoord);
    routeOverlays.push(marker);
  }

  // 终点（红色）使用 destinationStopGcj02Coordinates
  const destCoord = detail.value.destinationStopGcj02Coordinates;
  const destName = (detail.value.destinationStopName || "").trim();
  if (destCoord && Array.isArray(destCoord) && destCoord.length === 2) {
    const marker = new AMapInstance.Marker({
      position: destCoord,
      content: `<div style="display:flex;align-items:center;gap:4px;white-space:nowrap;"><div style="width:28px;height:28px;border-radius:50%;background:#ff4d4f;color:#fff;font-size:12px;line-height:28px;text-align:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">终</div><span style="font-size:12px;color:#333;background:rgba(255,255,255,0.9);padding:2px 6px;border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.2);">${destName}</span></div>`,
      offset: new AMapInstance.Pixel(-14, -14),
      map: map,
    });
    points.push(destCoord);
    routeOverlays.push(marker);
  }

  // 途经点（蓝色带序号）使用 waypoints 元素的 gcj02Coordinates
  const wps = waypoints.value || [];
  wps.forEach((wp, idx) => {
    const coord = wp.gcj02Coordinates;
    const wpName = (wp.stopName || wp.name || "").trim();
    if (coord && Array.isArray(coord) && coord.length === 2) {
      const marker = new AMapInstance.Marker({
        position: coord,
        content: `<div style="display:flex;align-items:center;gap:4px;white-space:nowrap;"><div style="width:24px;height:24px;border-radius:50%;background:#1890ff;color:#fff;font-size:12px;line-height:24px;text-align:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${idx + 1}</div><span style="font-size:12px;color:#333;background:rgba(255,255,255,0.9);padding:2px 6px;border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.2);">${wpName}</span></div>`,
        offset: new AMapInstance.Pixel(-12, -12),
        map: map,
      });
      points.push(coord);
      routeOverlays.push(marker);
    }
  });

  // 自适应视野：仅适配路线覆盖物，不包含围栏
  if (points.length > 0) {
    map.setFitView(routeOverlays, true, [80, 80, 80, 80]);
  }
};

// 获取任务详情
const loadDetail = async () => {
  const taskId = route.query.taskId;
  if (!taskId) {
    message.error("缺少任务ID");
    return;
  }
  detailLoading.value = true;
  try {
    const res = await getTaskDetail(taskId);
    if (res.code === 200 && res.data) {
      detail.value = res.data;
      drawRouteAndMarkers();
    } else {
      message.error(res.message || "获取详情失败");
    }
  } catch (error) {
    console.error("获取任务详情失败:", error);
    message.error("获取任务详情失败");
  } finally {
    detailLoading.value = false;
  }
};

// 审核提交
const reviewLoading = ref(false);
const reviewForm = reactive({
  comment: "",
});

const handleReview = async (status) => {
  // 驳回时审核意见为必填
  if (status === -1 && !reviewForm.comment.trim()) {
    message.warning("驳回时审核意见为必填项");
    return;
  }
  reviewLoading.value = true;
  try {
    const res = await reviewTask({
      taskId: route.query.taskId,
      status,
      comment: reviewForm.comment.trim(),
    });
    if (res.code === 200) {
      message.success(status === 1 ? "审核通过" : "已驳回");
      router.back();
    } else {
      message.error(res.message || "审核失败");
    }
  } catch (error) {
    console.error("审核失败:", error);
    message.error("审核失败");
  } finally {
    reviewLoading.value = false;
  }
};

const goBack = () => {
  router.back();
};

onMounted(async () => {
  await nextTick();
  await initMap();
  loadFences();
  await loadDetail();
});

onBeforeUnmount(() => {
  if (map) {
    map.destroy();
    map = null;
  }
});
</script>

<style lang="less" scoped>
.task-detail-page {
  display: flex;
  flex-direction: column;
  padding: 16px;
  height: calc(100vh - 70px);
  background-color: #ffffff;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
  flex-shrink: 0;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
  }
}

.page-body {
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 0;
}

.left-panel {
  width: 480px;
  flex-shrink: 0;
  overflow-y: auto;
  padding-right: 8px;
  background: #fafafa;
  padding: 20px;
  border-radius: 8px;
}

.right-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  position: relative;
  overflow: hidden;
  border-radius: 8px;

  .map-container {
    width: 100%;
    height: 100%;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    z-index: 10;

    .loading-text {
      font-size: 14px;
      color: #666;
    }
  }
}

.form-section {
  margin-bottom: 24px;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 16px;
    padding-left: 10px;
    border-left: 3px solid #1890ff;
    line-height: 1;
  }

  .waypoint-list {
    margin: 4px 0;
  }

  .point-row {
    align-items: center;

    .info-value {
      flex: 1;
    }
  }

  .point-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .start-dot {
    background: #52c41a;
  }

  .end-dot {
    background: #ff4d4f;
  }

  .point-index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #1890ff;
    color: #fff;
    font-size: 12px;
    flex-shrink: 0;
  }
}

.info-row {
  display: flex;
  align-items: center;
  padding: 6px 0;
  gap: 8px;

  .info-label {
    font-size: 13px;
    color: #8c8c8c;
    flex-shrink: 0;
    white-space: nowrap;
    min-width: 70px;
  }

  .info-value {
    font-size: 13px;
    color: #262626;
    word-break: break-all;
  }

  &.info-row-block {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

.review-actions {
  display: flex;
  justify-content: flex-end;
}
</style>
